import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { TextField } from 'app/components/form-fields';
import useFormField from 'app/hooks/form-fields/useFormField';
import useClickOutside from 'app/hooks/useClickOutside';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import SetNameIcon from 'assets/images/ill_set_name.svg';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SetUserNamesDialog.less';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import useInputPatternValidation from 'app/hooks/form-fields/useInputPatternValidation';
import {
  lettersNumbersAndSpecialsRegex,
  lettersOnlyWithSpaceAndHyphenRegex,
} from 'app/common/utils';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import { userInfoApi } from 'services/api/user-info/queries';
interface Props {
  onDismiss: () => void;
  onSuccess: () => void;
}

export default function SetUserNamesDialog({ onDismiss, onSuccess }: Props) {
  const { t } = useTranslation();

  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const { data: user } = useGetUserInfoQuery();
  const [updateUser, { isLoading }] = userInfoApi.useUpdateUserInfoMutation();

  const firstNameField = useFormField(user?.first_name ?? '');
  const lastNameField = useFormField(user?.last_name ?? '');
  const companyNameField = useFormField(user?.company_name ?? '');

  const { isPatternMatch: isNamePatternMatch, onKeyDown: onNameKeyDown } =
    useInputPatternValidation(lettersOnlyWithSpaceAndHyphenRegex);

  const { isPatternMatch: isCompanyNamePatternMatch } =
    useInputPatternValidation(lettersNumbersAndSpecialsRegex);

  const validationsFieldsMap = React.useMemo(
    () => ({
      firstName: isNamePatternMatch,
      lastName: isNamePatternMatch,
      companyName: isCompanyNamePatternMatch,
    }),
    [isNamePatternMatch, isCompanyNamePatternMatch],
  );

  useClickOutside(onDismiss);

  useEffect(() => {
    if (user) {
      firstNameField.setValue(user?.first_name ?? '');
      lastNameField.setValue(user?.last_name ?? '');
      companyNameField.setValue(user?.company_name ?? '');
    }
  }, [
    user,
    firstNameField.setValue,
    lastNameField.setValue,
    companyNameField.setValue,
  ]);

  function validateFields() {
    let isValid = true;

    if (!firstNameField.value || !isNamePatternMatch(firstNameField.value)) {
      isValid = false;
      firstNameField.setError(
        t('set_user_names_dialog.fields.first_name.is_required'),
      );
    }

    if (!lastNameField.value || !isNamePatternMatch(lastNameField.value)) {
      isValid = false;
      lastNameField.setError(
        t('set_user_names_dialog.fields.last_name.is_required'),
      );
    }

    if (
      companyNameField.value &&
      !isCompanyNamePatternMatch(companyNameField.value)
    ) {
      isValid = false;
      companyNameField.setError(t('Incorrect company name format'));
    }

    return isValid;
  }

  const handleChange = (
    event: InputChangeEvent,
    fieldKey: string,
    field: ReturnType<typeof useFormField<string>>,
  ) => {
    const inputValue = event.target.value as string;
    if (validationsFieldsMap[fieldKey](inputValue)) {
      field.setValue(inputValue);
      field.setError('');
    }
  };

  async function handleSave() {
    try {
      await updateUser({
        firstName: firstNameField.value,
        lastName: lastNameField.value,
        companyName: companyNameField.value,
      });
    } catch (error) {
      addErrorModal();
    }
    onSuccess();
  }

  return (
    <Dialog className="modal-center modal-remove-item modal-fulltext">
      <div className="set-user-names-dialog">
        <div className="set-user-names-dialog__header">
          <img
            className="set-user-names-dialog__image"
            src={SetNameIcon}
            alt="Set names"
            width={128}
            height={128}
          />

          <h2 className="set-user-names-dialog__title">
            {t('set_user_names_dialog.title')}
          </h2>

          <p
            className="set-user-names-dialog__subtitle"
            style={{ textAlign: 'center' }}
          >
            {t('set_user_names_dialog.subtitle')}
          </p>
        </div>

        <div className="set-user-names-dialog__content">
          <TextField
            label={t('set_user_names_dialog.fields.first_name.label')}
            placeholder={t(
              'set_user_names_dialog.fields.first_name.placeholder',
            )}
            required
            maxLength={64}
            disabled={isLoading}
            value={firstNameField.value}
            onKeyDown={onNameKeyDown}
            onChange={event => {
              handleChange(event, 'firstName', firstNameField);
            }}
            error={firstNameField.error}
          />

          <TextField
            label={t('set_user_names_dialog.fields.last_name.label')}
            placeholder={t(
              'set_user_names_dialog.fields.last_name.placeholder',
            )}
            required
            maxLength={64}
            disabled={isLoading}
            value={lastNameField.value}
            onKeyDown={onNameKeyDown}
            onChange={event => {
              handleChange(event, 'lastName', lastNameField);
            }}
            error={lastNameField.error}
          />

          <TextField
            label={t('set_user_names_dialog.fields.company_name.label')}
            placeholder={t(
              'set_user_names_dialog.fields.company_name.placeholder',
            )}
            maxLength={100}
            disabled={isLoading}
            value={companyNameField.value as string}
            onChange={event => {
              handleChange(event, 'companyName', companyNameField);
            }}
            error={companyNameField.error}
          />
        </div>

        <div className="set-user-names-dialog__actions-block">
          <Button disabled={isLoading} themeColor="dark" onClick={onDismiss}>
            {t('cancel')}
          </Button>

          <Button
            disabled={isLoading}
            themeColor="primary"
            onClick={() => validateFields() && handleSave()}
          >
            {t('save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
